import React from 'react';
import {
  AlertTriangle,
  ShieldAlert,
  ShieldCheck,
  Shield,
  TrendingDown,
  TrendingUp
} from 'lucide-react';
import { Badge } from './shared/Badge';

interface Phase1RedFlags {
  badEventQuickSwitchClean: boolean;
  rapidBackAndForth: boolean;
  ghostCarrierViolation: boolean;
  dumpAndReturnAfterIdle: boolean;
  shellCarrierOnlyBadEvents: boolean;
}

interface Phase2Indicators {
  relatedCarriers: boolean;
  sameOwner: boolean;
  sameAddress: boolean;
  samePhone: boolean;
  recentlyCreatedCarriers: boolean;
}

interface Phase3Whitelist {
  longGaps: boolean;
  legitimateSale: boolean;
  linearProgression: boolean;
  rentalUnit: boolean;
  postRevocationDispersal: boolean;
}

interface FraudSummaryCardsProps {
  level: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  fraudDetected: boolean;
  phase1RedFlags: Phase1RedFlags;
  phase2Indicators: Phase2Indicators;
  phase3Whitelist: Phase3Whitelist;
  reason?: string;
  compact?: boolean;
  onClick?: () => void;
}

// Summary card component
const SummaryCard: React.FC<{
  title: string;
  count: number;
  total: number;
  icon: React.ReactNode;
  color: 'red' | 'orange' | 'green' | 'blue';
  detected: string[];
}> = ({ title, count, total, icon, color, detected }) => {
  const bgColors = {
    red: 'bg-red-50 border-red-200',
    orange: 'bg-orange-50 border-orange-200',
    green: 'bg-green-50 border-green-200',
    blue: 'bg-blue-50 border-blue-200',
  };

  const textColors = {
    red: 'text-red-600',
    orange: 'text-orange-600',
    green: 'text-green-600',
    blue: 'text-blue-600',
  };

  const progressColors = {
    red: 'bg-red-500',
    orange: 'bg-orange-500',
    green: 'bg-green-500',
    blue: 'bg-blue-500',
  };

  return (
    <div className={`p-4 rounded-lg border ${bgColors[color]} transition-all hover:shadow-md`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-700">{title}</span>
        <div className={textColors[color]}>{icon}</div>
      </div>
      <div className={`text-3xl font-bold ${textColors[color]}`}>
        {count}/{total}
      </div>
      {/* Progress bar */}
      <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
        <div
          className={`h-full ${progressColors[color]} transition-all duration-300`}
          style={{ width: `${(count / total) * 100}%` }}
        />
      </div>
      {/* Detected items */}
      {count > 0 && detected.length > 0 && (
        <div className="mt-2 space-y-1">
          {detected.slice(0, 2).map((item, idx) => (
            <div key={idx} className="text-xs text-gray-600 truncate" title={item}>
              {item}
            </div>
          ))}
          {detected.length > 2 && (
            <div className="text-xs text-gray-500">+{detected.length - 2} more</div>
          )}
        </div>
      )}
    </div>
  );
};

// Risk level display component
const RiskLevelCard: React.FC<{
  level: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  fraudDetected: boolean;
  reason?: string;
}> = ({ level, fraudDetected, reason }) => {
  const levelConfig = {
    LOW: {
      bg: 'bg-green-50 border-green-300',
      icon: <ShieldCheck className="w-10 h-10 text-green-500" />,
      badge: 'success',
      text: 'text-green-800',
    },
    MEDIUM: {
      bg: 'bg-yellow-50 border-yellow-300',
      icon: <Shield className="w-10 h-10 text-yellow-500" />,
      badge: 'warning',
      text: 'text-yellow-800',
    },
    HIGH: {
      bg: 'bg-orange-50 border-orange-300',
      icon: <ShieldAlert className="w-10 h-10 text-orange-500" />,
      badge: 'warning',
      text: 'text-orange-800',
    },
    CRITICAL: {
      bg: 'bg-red-50 border-red-300',
      icon: <AlertTriangle className="w-10 h-10 text-red-500" />,
      badge: 'danger',
      text: 'text-red-800',
    },
  };

  const config = levelConfig[level];

  return (
    <div className={`p-4 rounded-lg border-2 ${config.bg} transition-all hover:shadow-md`}>
      <div className="flex items-center gap-4">
        {config.icon}
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Badge variant={config.badge as any} size="lg">{level} RISK</Badge>
            {fraudDetected && (
              <Badge variant="danger" size="sm">FRAUD DETECTED</Badge>
            )}
          </div>
          {reason && (
            <p className={`text-sm mt-1 ${config.text}`}>{reason}</p>
          )}
        </div>
        {level === 'LOW' && !fraudDetected && (
          <TrendingDown className="w-8 h-8 text-green-500" />
        )}
        {(level === 'HIGH' || level === 'CRITICAL') && (
          <TrendingUp className="w-8 h-8 text-red-500" />
        )}
      </div>
    </div>
  );
};

export const FraudSummaryCards: React.FC<FraudSummaryCardsProps> = ({
  level,
  fraudDetected,
  phase1RedFlags,
  phase2Indicators,
  phase3Whitelist,
  reason,
  compact = false,
  onClick
}) => {
  // Count flags in each phase
  const phase1Count = Object.values(phase1RedFlags).filter(Boolean).length;
  const phase2Count = Object.values(phase2Indicators).filter(Boolean).length;
  const phase3Count = Object.values(phase3Whitelist).filter(Boolean).length;

  // Get detected items names
  const phase1Detected = Object.entries(phase1RedFlags)
    .filter(([_, v]) => v)
    .map(([k]) => {
      const labels: Record<string, string> = {
        badEventQuickSwitchClean: 'Bad Event Quick Switch',
        rapidBackAndForth: 'Ping-Pong Pattern',
        ghostCarrierViolation: 'Ghost Carrier',
        dumpAndReturnAfterIdle: 'Dump & Return',
        shellCarrierOnlyBadEvents: 'Shell Carrier',
      };
      return labels[k] || k;
    });

  const phase2Detected = Object.entries(phase2Indicators)
    .filter(([_, v]) => v)
    .map(([k]) => {
      const labels: Record<string, string> = {
        relatedCarriers: 'Related Carriers',
        sameOwner: 'Same Owner',
        sameAddress: 'Same Address',
        samePhone: 'Same Phone',
        recentlyCreatedCarriers: 'New Carriers',
      };
      return labels[k] || k;
    });

  const phase3Detected = Object.entries(phase3Whitelist)
    .filter(([_, v]) => v)
    .map(([k]) => {
      const labels: Record<string, string> = {
        longGaps: 'Long Gaps',
        legitimateSale: 'Legitimate Sale',
        linearProgression: 'Linear Progression',
        rentalUnit: 'Rental Unit',
        postRevocationDispersal: 'Post-Revocation',
      };
      return labels[k] || k;
    });

  // Compact view for dashboard cards
  if (compact) {
    return (
      <div
        className={`p-3 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
          level === 'CRITICAL' ? 'bg-red-50 border-red-300' :
          level === 'HIGH' ? 'bg-orange-50 border-orange-300' :
          level === 'MEDIUM' ? 'bg-yellow-50 border-yellow-300' :
          'bg-green-50 border-green-300'
        }`}
        onClick={onClick}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {level === 'CRITICAL' || level === 'HIGH' ? (
              <AlertTriangle className={`w-5 h-5 ${level === 'CRITICAL' ? 'text-red-500' : 'text-orange-500'}`} />
            ) : level === 'MEDIUM' ? (
              <Shield className="w-5 h-5 text-yellow-500" />
            ) : (
              <ShieldCheck className="w-5 h-5 text-green-500" />
            )}
            <span className="font-semibold text-gray-900">{level}</span>
          </div>
          <div className="flex gap-1">
            {phase1Count > 0 && (
              <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-xs rounded font-medium">
                P1:{phase1Count}
              </span>
            )}
            {phase2Count > 0 && (
              <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 text-xs rounded font-medium">
                P2:{phase2Count}
              </span>
            )}
            {phase3Count > 0 && (
              <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-xs rounded font-medium">
                P3:{phase3Count}
              </span>
            )}
          </div>
        </div>
        {fraudDetected && (
          <div className="mt-1 text-xs text-red-600 font-medium">Fraud Detected</div>
        )}
      </div>
    );
  }

  // Full view with all cards
  return (
    <div className="space-y-4" onClick={onClick}>
      {/* Risk Level Card */}
      <RiskLevelCard level={level} fraudDetected={fraudDetected} reason={reason} />

      {/* Phase Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SummaryCard
          title="Phase 1: Red Flags"
          count={phase1Count}
          total={5}
          icon={<AlertTriangle className="w-5 h-5" />}
          color="red"
          detected={phase1Detected}
        />
        <SummaryCard
          title="Phase 2: Indicators"
          count={phase2Count}
          total={5}
          icon={<ShieldAlert className="w-5 h-5" />}
          color="orange"
          detected={phase2Detected}
        />
        <SummaryCard
          title="Phase 3: Whitelist"
          count={phase3Count}
          total={5}
          icon={<ShieldCheck className="w-5 h-5" />}
          color="green"
          detected={phase3Detected}
        />
      </div>
    </div>
  );
};

// Mini badge for inline display
export const FraudMiniIndicator: React.FC<{
  level: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  phase1Count: number;
  phase2Count: number;
  phase3Count: number;
  onClick?: () => void;
}> = ({ level, phase1Count, phase2Count, phase3Count, onClick }) => {
  const levelColors = {
    LOW: 'bg-green-100 text-green-800 border-green-300',
    MEDIUM: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    HIGH: 'bg-orange-100 text-orange-800 border-orange-300',
    CRITICAL: 'bg-red-100 text-red-800 border-red-300',
  };

  return (
    <div
      className={`inline-flex items-center gap-1 px-2 py-1 rounded border text-xs font-medium cursor-pointer hover:opacity-80 ${levelColors[level]}`}
      onClick={onClick}
    >
      <span>{level}</span>
      <span className="opacity-60">|</span>
      <span className="text-red-600">{phase1Count}</span>
      <span className="text-orange-600">{phase2Count}</span>
      <span className="text-green-600">{phase3Count}</span>
    </div>
  );
};

export default FraudSummaryCards;
